import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Save, Upload, Trash2, Plus, GripVertical,
  BookOpen, HelpCircle, FileText, Loader2, Image as ImageIcon,
  ChevronDown, ChevronUp, Eye, Shield, Lock, KeyRound
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SlideImage = ({ slide, catalogId, token }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!slide.has_image) return;
    fetch(`${API}/courses/${catalogId}/slides/${slide.id}/image`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.blob())
      .then(blob => setSrc(URL.createObjectURL(blob)))
      .catch(() => {});
    return () => { if (src) URL.revokeObjectURL(src); };
  }, [slide.id, slide.has_image, slide._imageTs]);
  if (!src) return <div className="h-20 w-32 bg-slate-100 rounded animate-pulse" />;
  return <img src={src} alt="" className="h-20 rounded border border-slate-200 object-contain bg-slate-50" />;
};

const slideTypeConfig = {
  lesson: { label: 'Lecon', color: 'bg-teal-100 text-teal-700', icon: BookOpen },
  quiz: { label: 'Quiz', color: 'bg-amber-100 text-amber-700', icon: HelpCircle },
  exercise: { label: 'Exercice', color: 'bg-purple-100 text-purple-700', icon: FileText },
};

const AdminEditorPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, token, loading: authLoading, login } = useAuth();

  const [course, setCourse] = useState(null);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSlide, setExpandedSlide] = useState(null);
  const fileInputRef = useRef({});

  // Admin auth gate
  const [adminVerified, setAdminVerified] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCourse = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/courses/${courseId}/edit`, { headers });
      setCourse(res.data);
      setSlides(res.data.slides || []);
      if (res.data.slides?.length > 0) setExpandedSlide(0);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Acces reserve aux administrateurs");
        navigate('/admin');
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  }, [courseId, token]);

  useEffect(() => {
    if (token && adminVerified) fetchCourse();
  }, [token, adminVerified, fetchCourse]);

  const saveSlide = async (slideIndex) => {
    const slide = slides[slideIndex];
    setSaving(true);
    try {
      const payload = {
        title: slide.title,
        content: slide.content,
        narrator_text: slide.narrator_text,
        type: slide.type,
      };
      if (slide.type === 'quiz') payload.quiz_data = slide.quiz_data;
      if (slide.type === 'exercise') payload.exercise_data = slide.exercise_data;

      await axios.put(`${API}/admin/courses/${courseId}/slides/${slide.id}`, payload, { headers });
      toast.success(`Slide "${slide.title}" sauvegardee`);
    } catch {
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const saveCourseInfo = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/courses/${courseId}`, {
        title: course.title,
        description: course.description,
        category: course.category,
        level: course.level,
        duration: course.duration,
      }, { headers });
      toast.success("Informations mises à jour");
    } catch {
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (slideIndex, file) => {
    const slide = slides[slideIndex];
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/admin/courses/${courseId}/slides/${slide.id}/upload-image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      const updated = [...slides];
      updated[slideIndex] = { ...updated[slideIndex], has_image: true, _imageTs: Date.now() };
      setSlides(updated);
      toast.success("Image uploadee");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'upload");
    }
  };

  const deleteImage = async (slideIndex) => {
    const slide = slides[slideIndex];
    try {
      await axios.delete(`${API}/admin/courses/${courseId}/slides/${slide.id}/image`, { headers });
      const updated = [...slides];
      updated[slideIndex] = { ...updated[slideIndex], has_image: false };
      setSlides(updated);
      toast.success("Image supprimee");
    } catch {
      toast.error("Erreur");
    }
  };

  const addSlide = async (type) => {
    if (slides.length >= 200) {
      toast.error("Maximum 200 slides par formation");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/courses/${courseId}/slides`, {
        title: type === 'quiz' ? 'Quiz' : type === 'exercise' ? 'Exercice pratique' : 'Nouvelle lecon',
        type,
        content: ''
      }, { headers });
      await fetchCourse();
      toast.success("Slide ajoutee");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteSlide = async (slideIndex) => {
    const slide = slides[slideIndex];
    if (!window.confirm(`Supprimer la slide "${slide.title}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/courses/${courseId}/slides/${slide.id}`, { headers });
      await fetchCourse();
      toast.success("Slide supprimee");
    } catch {
      toast.error("Erreur");
    }
  };

  const updateSlideLocal = (index, field, value) => {
    const updated = [...slides];
    updated[index] = { ...updated[index], [field]: value };
    setSlides(updated);
  };

  const updateQuizQuestion = (slideIndex, qIndex, field, value) => {
    const updated = [...slides];
    const questions = [...(updated[slideIndex].quiz_data?.questions || [])];
    questions[qIndex] = { ...questions[qIndex], [field]: value };
    updated[slideIndex] = { ...updated[slideIndex], quiz_data: { questions } };
    setSlides(updated);
  };

  const addQuizQuestion = (slideIndex) => {
    const updated = [...slides];
    const questions = [...(updated[slideIndex].quiz_data?.questions || [])];
    questions.push({ question: '', options: ['', '', '', ''], correct_index: 0, explanation: '' });
    updated[slideIndex] = { ...updated[slideIndex], quiz_data: { questions } };
    setSlides(updated);
  };

  const removeQuizQuestion = (slideIndex, qIndex) => {
    const updated = [...slides];
    const questions = (updated[slideIndex].quiz_data?.questions || []).filter((_, i) => i !== qIndex);
    updated[slideIndex] = { ...updated[slideIndex], quiz_data: { questions } };
    setSlides(updated);
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  }

  if (!adminVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900" data-testid="editor-login-gate">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4"><Shield className="w-8 h-8 text-teal-600" /></div>
              <h2 className="text-xl font-bold text-slate-900">Editeur de formation</h2>
              <p className="text-sm text-slate-500 mt-1">Connexion administrateur requise</p>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoginLoading(true);
              setLoginError('');
              try {
                const u = await login(loginForm.email, loginForm.password);
                if (!u.is_admin) { setLoginError("Droits administrateur requis."); setLoginLoading(false); return; }
                setAdminVerified(true);
              } catch (err) { setLoginError(err.response?.data?.detail || 'Identifiants incorrects'); }
              finally { setLoginLoading(false); }
            }} className="space-y-4">
              <Input type="email" value={loginForm.email} onChange={(e) => setLoginForm({...loginForm, email: e.target.value})} placeholder="admin@aerh.pf" required data-testid="editor-email" />
              <Input type="password" value={loginForm.password} onChange={(e) => setLoginForm({...loginForm, password: e.target.value})} placeholder="Mot de passe" required data-testid="editor-password" />
              {loginError && <p className="text-sm text-red-600 bg-red-50 p-2 rounded flex items-center gap-2"><Lock className="w-4 h-4" />{loginError}</p>}
              <Button type="submit" disabled={loginLoading} className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                {loginLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}Connexion
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center pt-24"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  }

  if (!course) return null;

  return (
    <div className="min-h-screen bg-slate-100 pt-20 pb-16" data-testid="admin-editor">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-20 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-teal-600" data-testid="btn-back-admin">
            <ArrowLeft className="w-4 h-4" /> Admin
          </button>
          <h2 className="text-sm font-bold text-slate-900 truncate mx-4">{course.title}</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">{slides.length} slides</span>
            <Button size="sm" onClick={() => navigate(`/cours/${course.catalog_id}`)} variant="outline" className="text-xs h-8" data-testid="btn-preview">
              <Eye className="w-3 h-3 mr-1" /> Aperçu
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Course info */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="course-info-section">
          <h3 className="font-bold text-slate-900 mb-3">Informations de la formation</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Titre</label>
              <Input value={course.title} onChange={(e) => setCourse({...course, title: e.target.value})} data-testid="input-course-title" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Categorie</label>
              <Input value={course.category || ''} onChange={(e) => setCourse({...course, category: e.target.value})} data-testid="input-course-category" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Niveau</label>
              <select value={course.level || ''} onChange={(e) => setCourse({...course, level: e.target.value})} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="select-course-level">
                <option value="Débutant">Débutant</option>
                <option value="Intermédiaire">Intermédiaire</option>
                <option value="Avancé">Avancé</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Duree</label>
              <Input value={course.duration || ''} onChange={(e) => setCourse({...course, duration: e.target.value})} data-testid="input-course-duration" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-500 mb-1">Description</label>
              <textarea value={course.description || ''} onChange={(e) => setCourse({...course, description: e.target.value})} rows={2} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none" data-testid="input-course-desc" />
            </div>
          </div>
          <Button size="sm" onClick={saveCourseInfo} disabled={saving} className="mt-3 bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-save-course-info">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Sauvegarder
          </Button>
        </div>

        {/* Slides */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-900">Slides ({slides.length})</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => addSlide('lesson')} className="text-xs h-8" data-testid="btn-add-lesson">
                <Plus className="w-3 h-3 mr-1" /> Lecon
              </Button>
              <Button size="sm" variant="outline" onClick={() => addSlide('quiz')} className="text-xs h-8" data-testid="btn-add-quiz">
                <Plus className="w-3 h-3 mr-1" /> Quiz
              </Button>
              <Button size="sm" variant="outline" onClick={() => addSlide('exercise')} className="text-xs h-8" data-testid="btn-add-exercise">
                <Plus className="w-3 h-3 mr-1" /> Exercice
              </Button>
            </div>
          </div>

          {slides.map((slide, idx) => {
            const cfg = slideTypeConfig[slide.type] || slideTypeConfig.lesson;
            const isExpanded = expandedSlide === idx;
            const Icon = cfg.icon;
            return (
              <div key={slide.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`slide-editor-${idx}`}>
                {/* Slide header */}
                <button
                  onClick={() => setExpandedSlide(isExpanded ? null : idx)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                >
                  <GripVertical className="w-4 h-4 text-slate-300" />
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${cfg.color}`}>{cfg.label}</span>
                  <span className="text-sm font-medium text-slate-900 flex-1 truncate">{slide.title}</span>
                  <div className="flex items-center gap-2">
                    {slide.has_image && <ImageIcon className="w-3.5 h-3.5 text-teal-500" />}
                    <span className="text-xs text-slate-400">#{idx + 1}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} className="border-t border-slate-100 px-4 py-4 space-y-4">
                    {/* Title */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Titre de la slide</label>
                      <Input value={slide.title} onChange={(e) => updateSlideLocal(idx, 'title', e.target.value)} />
                    </div>

                    {/* Image upload */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Image</label>
                      {slide.has_image ? (
                        <div className="flex items-center gap-3">
                          <SlideImage slide={slide} catalogId={course.catalog_id} token={token} />
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => fileInputRef.current[idx]?.click()}>
                              <Upload className="w-3 h-3 mr-1" /> Remplacer
                            </Button>
                            <Button size="sm" variant="ghost" className="text-xs h-8 text-red-500" onClick={() => deleteImage(idx)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => fileInputRef.current[idx]?.click()} data-testid={`btn-upload-image-${idx}`}>
                          <Upload className="w-3 h-3 mr-1" /> Uploader une image
                        </Button>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={(el) => fileInputRef.current[idx] = el}
                        onChange={(e) => { if (e.target.files[0]) uploadImage(idx, e.target.files[0]); }}
                      />
                    </div>

                    {/* Content - for lessons */}
                    {slide.type === 'lesson' && (
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Contenu (HTML)</label>
                        <textarea
                          value={slide.content}
                          onChange={(e) => updateSlideLocal(idx, 'content', e.target.value)}
                          rows={8}
                          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono resize-y"
                          data-testid={`textarea-content-${idx}`}
                        />
                      </div>
                    )}

                    {/* Quiz editor */}
                    {slide.type === 'quiz' && (
                      <div data-testid={`quiz-editor-${idx}`}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-semibold text-slate-500">{(slide.quiz_data?.questions || []).length} questions (min. 10 recommandees)</label>
                          <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => addQuizQuestion(idx)}>
                            <Plus className="w-3 h-3 mr-1" /> Question
                          </Button>
                        </div>
                        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                          {(slide.quiz_data?.questions || []).map((q, qi) => (
                            <div key={qi} className="bg-slate-50 rounded-lg p-3 text-sm">
                              <div className="flex items-start gap-2 mb-2">
                                <span className="text-xs font-bold text-slate-400 mt-2">Q{qi + 1}</span>
                                <Input value={q.question} onChange={(e) => updateQuizQuestion(idx, qi, 'question', e.target.value)} placeholder="Question..." className="flex-1 text-sm" />
                                <button onClick={() => removeQuizQuestion(idx, qi)} className="text-red-400 hover:text-red-600 mt-1"><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                              <div className="grid grid-cols-2 gap-2 ml-6">
                                {(q.options || []).map((opt, oi) => (
                                  <div key={oi} className="flex items-center gap-1">
                                    <input
                                      type="radio"
                                      name={`q-${idx}-${qi}`}
                                      checked={q.correct_index === oi}
                                      onChange={() => updateQuizQuestion(idx, qi, 'correct_index', oi)}
                                      className="accent-teal-600"
                                    />
                                    <Input
                                      value={opt}
                                      onChange={(e) => {
                                        const opts = [...(q.options || [])];
                                        opts[oi] = e.target.value;
                                        updateQuizQuestion(idx, qi, 'options', opts);
                                      }}
                                      className="text-xs h-8"
                                      placeholder={`Option ${oi + 1}`}
                                    />
                                  </div>
                                ))}
                              </div>
                              <div className="ml-6 mt-2">
                                <Input value={q.explanation || ''} onChange={(e) => updateQuizQuestion(idx, qi, 'explanation', e.target.value)} placeholder="Explication de la réponse..." className="text-xs" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Exercise editor */}
                    {slide.type === 'exercise' && (
                      <div data-testid={`exercise-editor-${idx}`}>
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 mb-1">Instructions</label>
                          <textarea
                            value={slide.exercise_data?.instructions || ''}
                            onChange={(e) => updateSlideLocal(idx, 'exercise_data', { ...slide.exercise_data, instructions: e.target.value })}
                            rows={3}
                            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-y"
                          />
                        </div>
                        <div className="mt-3">
                          <label className="block text-xs font-semibold text-slate-500 mb-1">Réponse attendue</label>
                          <textarea
                            value={slide.exercise_data?.expected_answer || ''}
                            onChange={(e) => updateSlideLocal(idx, 'exercise_data', { ...slide.exercise_data, expected_answer: e.target.value })}
                            rows={3}
                            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-y"
                          />
                        </div>
                      </div>
                    )}

                    {/* Narrator text */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Texte voix off</label>
                      <textarea
                        value={slide.narrator_text || ''}
                        onChange={(e) => updateSlideLocal(idx, 'narrator_text', e.target.value)}
                        rows={2}
                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-y"
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <Button size="sm" onClick={() => saveSlide(idx)} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-8" data-testid={`btn-save-slide-${idx}`}>
                        {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Sauvegarder cette slide
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteSlide(idx)} className="text-xs h-8 text-red-500 hover:bg-red-50" data-testid={`btn-delete-slide-${idx}`}>
                        <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                      </Button>
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminEditorPage;
